import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Namaste! Welcome to Ayodhya Dairy. I'm your virtual assistant, here to help you with information about our products, company, and services. How may I assist you today?",
      sender: 'bot'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase().trim();

    // Help command
    if (message === 'help' || message === 'menu' || message === 'options') {
      return "I can assist you with the following:\n\n• Products & Pricing Information\n• Company Details & History\n• Contact Information\n• Order Placement\n• Health Benefits of Our Products\n• Delivery Services\n• Quality Standards & Certifications\n\nPlease feel free to ask me anything about Ayodhya Dairy.";
    }

    // Greetings - expanded
    if (message.match(/\b(hello|hi|hey|namaste|namaskar|good morning|good afternoon|good evening|gm|gn)\b/)) {
      const greetings = [
        "Namaste! How may I assist you today?",
        "Hello! Welcome to Ayodhya Dairy. How can I help you?",
        "Good day! I'm here to assist you. What would you like to know?",
        "Namaste! How may I help you with Ayodhya Dairy today?"
      ];
      return greetings[Math.floor(Math.random() * greetings.length)];
    }

    // Thank you / appreciation
    if (message.match(/\b(thank|thanks|thank you|dhanyavad|appreciate|great|good|nice|awesome|perfect)\b/)) {
      return "You're most welcome! Is there anything else I can help you with regarding Ayodhya Dairy?";
    }

    // Specific product queries
    if (message.match(/\b(pure milk|regular milk|normal milk)\b/)) {
      return "Our Pure Milk is priced at ₹60 per liter. It is fresh, pure milk sourced from grass-fed cows, rich in essential nutrients. Ideal for daily consumption. Would you like information about delivery options or to place an order?";
    }

    if (message.match(/\b(a2|a2 milk)\b/)) {
      return "A2 Milk is our premium offering at ₹80 per liter. It contains natural A2 proteins that are easier to digest, making it suitable for individuals with sensitive digestive systems. Would you like additional information or to place an order?";
    }

    if (message.match(/\b(ghee|clarified butter|desi ghee)\b/)) {
      return "Our Pure Ghee is available at ₹450 for 500g. It is crafted using traditional methods to ensure authentic flavor and is 100% pure and natural. Perfect for culinary use and religious ceremonies. Would you like to place an order?";
    }

    if (message.match(/\b(curd|dahi|yogurt)\b/)) {
      return "Fresh Curd is priced at ₹50 for 500g. It is a creamy, probiotic-rich product that supports digestive health. Made fresh daily to ensure quality. Would you like delivery information?";
    }

    if (message.match(/\b(paneer|cottage cheese)\b/)) {
      return "Fresh Paneer is available at ₹280 per kilogram. It is soft, fresh paneer made daily for your culinary needs. Perfect for cooking and available for home delivery.";
    }

    if (message.match(/\b(buttermilk|chaas|lassi)\b/)) {
      return "Buttermilk is priced at ₹35 for 500ml. It is a refreshing traditional beverage, perfect for warm weather and beneficial for digestion.";
    }

    if (message.match(/\b(cream|malai)\b/)) {
      return "Cream is available at ₹120 for 200ml. It is rich, fresh cream perfect for desserts and traditional recipes, maintaining premium quality standards.";
    }

    // Products - general
    if (message.match(/\b(product|products|what do you sell|what do you have|items|offerings)\b/)) {
      return "We offer 8 premium dairy products:\n\n• Pure Milk - ₹60/L\n• A2 Milk - ₹80/L\n• Pure Ghee - ₹450/500g\n• Fresh Curd - ₹50/500g\n• Fresh Paneer - ₹280/kg\n• Buttermilk - ₹35/500ml\n• Flavored Milk - ₹40/200ml\n• Cream - ₹120/200ml\n\nAll products are 100% pure and natural. Which product would you like to know more about?";
    }

    // Price queries - expanded
    if (message.match(/\b(price|prices|cost|costs|how much|rate|rates|pricing|expensive|cheap|affordable)\b/)) {
      return "Our product pricing:\n\n• Pure Milk: ₹60/L\n• A2 Milk: ₹80/L\n• Pure Ghee: ₹450/500g\n• Fresh Curd: ₹50/500g\n• Fresh Paneer: ₹280/kg\n• Buttermilk: ₹35/500ml\n• Flavored Milk: ₹40/200ml\n• Cream: ₹120/200ml\n\nNote: Special pricing is available for bulk orders. Please contact us for details.";
    }

    // About company - expanded
    if (message.match(/\b(about|company|who are you|tell me about|history|story|background|established|since when|founded)\b/)) {
      return "Ayodhya Dairy has been serving families since 2008 with pure, traditional dairy products. We're inspired by the sacred traditions of Ayodhya and combine age-old methods with modern hygiene standards. Our products embody purity, health, and timeless values. Trusted by 50K+ families across Uttar Pradesh. We're FSSAI certified and committed to quality!";
    }

    // Contact - expanded
    if (message.match(/\b(contact|phone|mobile|number|call|email|address|location|where|visit|store|shop|office)\b/)) {
      return "Contact Information:\n\nPhone: +91 98765 43210\nEmail: info@ayodhyadairy.com\nAddress: Rani Mandir Road, Ayodhya, Uttar Pradesh 224123\n\nWorking Hours:\nMonday to Saturday: 9:00 AM - 8:00 PM\nSunday: 7:00 AM - 6:00 PM\n\nYou may also use the contact form on our website for inquiries.";
    }

    // Order - expanded
    if (message.match(/\b(order|buy|purchase|get|want|need|take|book|place order|how to order|how can i order|order now)\b/)) {
      return "To place an order, you can:\n\n1. Click 'Order Now' on any product card on our website\n2. Call us directly at +91 98765 43210\n3. Fill out the contact form and select 'Bulk Order' as the subject\n4. Visit our store in Ayodhya\n\nWe offer home delivery services. For bulk orders, please contact us for special pricing and arrangements.";
    }

    // Delivery - expanded
    if (message.match(/\b(delivery|deliver|home delivery|doorstep|ship|shipping|when|time|charges|free delivery|delivery charge)\b/)) {
      return "Yes, we provide home delivery services in Ayodhya and surrounding areas.\n\n• Place orders by phone: +91 98765 43210\n• Or through our website\n• Delivery charges and timings depend on your location\n• Please contact us for specific delivery details\n\nFor bulk orders, we offer special delivery arrangements and pricing.";
    }

    // Quality - expanded
    if (message.match(/\b(quality|pure|natural|certified|fssai|certification|standard|fresh|authentic|traditional|organic)\b/)) {
      return "Our quality standards:\n\n• 100% Pure & Natural Products\n• FSSAI Certified\n• Traditional Manufacturing Methods\n• Modern Hygiene Standards\n• Sourced from Grass-fed Cows\n• Authentic Taste Preservation\n• High Nutritional Value\n\nWe maintain the highest quality standards while preserving traditional values and methods.";
    }

    // Health - expanded
    if (message.match(/\b(health|healthy|benefit|benefits|nutrition|nutritious|good for|vitamin|calcium|protein|probiotic)\b/)) {
      return "Our dairy products provide numerous health benefits:\n\n• Strong Bones - Rich in calcium and vitamin D\n• Brain Function - Essential nutrients for cognitive health\n• Growth Support - Complete proteins for development\n• Digestive Health - Natural probiotics\n• Heart Health - Balanced fats\n• Immunity - Natural antibodies\n• Energy - Sustained energy levels\n• Skin Health - Vitamins and healthy fats\n\nOur products are suitable for the entire family and support overall wellness.";
    }

    // Questions about availability
    if (message.match(/\b(available|stock|in stock|out of stock|have|do you have|is available)\b/)) {
      return "Yes, all our products are currently available. We maintain fresh stock daily to ensure quality. For current availability of specific products or bulk quantities, please contact us at +91 98765 43210. Our operating hours are Monday to Saturday: 9:00 AM - 8:00 PM, and Sunday: 7:00 AM - 6:00 PM.";
    }

    // Payment methods
    if (message.match(/\b(payment|pay|cash|card|online|upi|gpay|phonepe|credit|debit|payment method)\b/)) {
      return "We accept the following payment methods:\n\n• Cash on Delivery\n• Credit/Debit Cards\n• UPI (GPay, PhonePe, and other UPI apps)\n• Online Payment Gateway\n\nFor detailed information about payment options, please contact us at +91 98765 43210.";
    }

    // Timing/Hours
    if (message.match(/\b(time|timing|hours|open|closed|when|available time|working|business hours)\b/)) {
      return "Our business hours:\n\nMonday to Saturday: 9:00 AM - 8:00 PM\nSunday: 7:00 AM - 6:00 PM\n\nYou may visit our store or contact us during these hours. For delivery orders, we accept requests through our website at any time.";
    }

    // Yes/No responses
    if (message.match(/\b(yes|yeah|yep|sure|ok|okay|alright|definitely|absolutely)\b/)) {
      return "Excellent! What additional information would you like? I can assist you with product details, pricing, order placement, delivery services, or any other inquiries about Ayodhya Dairy.";
    }

    if (message.match(/\b(no|nope|not|don't|don't want|not interested)\b/)) {
      return "Understood. Is there anything else I can assist you with? Please feel free to ask about our products, company information, or services at any time.";
    }

    // Questions
    if (message.includes('?') || message.match(/\b(what|when|where|who|why|how|which|can|could|would|should|is|are|do|does|did)\b/)) {
      // Try to extract context from question
      if (message.match(/\b(what|which)\b.*\b(product|item|milk|ghee|curd|paneer)\b/)) {
        return "We have 8 products: Pure Milk, A2 Milk, Pure Ghee, Fresh Curd, Fresh Paneer, Buttermilk, Flavored Milk, and Cream. Which one would you like to know more about?";
      }
      if (message.match(/\b(how|what)\b.*\b(order|buy|purchase|get)\b/)) {
        return "You can order by:\n1. Clicking 'Order Now' on product cards\n2. Calling +91 98765 43210\n3. Using our contact form\n4. Visiting our store\n\nWe offer home delivery! Need more details?";
      }
      return "That's a great question! I can help you with information about our products, prices, company, contact details, orders, delivery, and more. Could you be more specific about what you'd like to know?";
    }

    // Default - more helpful
    const suggestions = [
      "I'd be happy to assist you. You can inquire about:\n\n• Our 8 dairy products and pricing\n• Order placement procedures\n• Company information and history\n• Contact details and location\n• Delivery services\n• Health benefits of our products\n\nWhat specific information would you like?",
      "I'm here to help. You may ask about:\n\n• Products: 'What products do you offer?'\n• Pricing: 'What are your prices?'\n• Orders: 'How can I place an order?'\n• Contact: 'What is your contact number?'\n\nPlease feel free to ask any questions.",
      "I can provide information about:\n\n• Products & Pricing\n• Company Details\n• Contact Information\n• Ordering Process\n• Delivery Services\n\nWhat would you like to know more about?"
    ];
    return suggestions[Math.floor(Math.random() * suggestions.length)];
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = inputValue;
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    setInputValue('');

    // Simulate bot thinking
    setTimeout(() => {
      setMessages(prev => {
        const botResponse = getBotResponse(userMessage);
        return [...prev, { text: botResponse, sender: 'bot' }];
      });
    }, 500);
  };

  return (
    <>
      <div className={`chatbot-container ${isOpen ? 'open' : ''}`}>
        <div className="chatbot-header">
          <div className="chatbot-header-content">
            <div className="chatbot-logo">
              <div className="chatbot-logo-icon">AD</div>
              <div>
                <h3>Ayodhya Dairy</h3>
                <p>Online Support</p>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)}>
              ×
            </button>
          </div>
        </div>
        <div className="chatbot-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender}`}>
              <div className="message-content">
                {msg.text.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < msg.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <form className="chatbot-input-form" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message..."
            className="chatbot-input"
          />
          <button type="submit" className="chatbot-send-btn">
            <span>Send</span>
          </button>
        </form>
      </div>
      <button className="chatbot-toggle" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? (
          <span className="chatbot-close-icon">×</span>
        ) : (
          <svg className="chatbot-robot-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
            <circle cx="9" cy="11" r="1.5" fill="currentColor"/>
            <circle cx="15" cy="11" r="1.5" fill="currentColor"/>
            <path d="M8 15h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M10 6V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" fill="none"/>
          </svg>
        )}
        <span className="chatbot-toggle-text">Chat with us</span>
      </button>
    </>
  );
};

export default Chatbot;

